import { MenuItemsType } from "@/layouts/carbon/carbon-menu-items";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { Roles } from "@/utilities/types/Users";

interface UserFeature {
  enabled: boolean;
  feature: {
    enabled: boolean;
    type: EFeatureType;
  };
}

/**
 * Check if a user has permission to access a menu item based on their role
 */
export const hasPermission = (
  currentRole: Roles | undefined,
  permissions: Roles[] | undefined
): boolean => {
  if (!currentRole || !permissions) return false;
  return permissions.includes(currentRole);
};

/**
 * Check if user has access to an advance feature
 * Admin always has access to all advance features
 * If no advanceFeatureType specified, return true (no restriction)
 */
export const hasAdvanceFeatureAccess = (
  currentRole: Roles | undefined,
  userFeatures: UserFeature[] | undefined,
  advanceFeatureType: EFeatureType | undefined
): boolean => {
  // If no advance feature type is specified, no restriction
  if (!advanceFeatureType) return true;

  // Admin always has access to all advance features
  if (currentRole === Roles.ADMIN) return true;

  // If no user features or not logged in, no access
  if (!userFeatures || !currentRole) return false;

  // Check if user has the specific feature enabled
  return userFeatures.some(
    (feat) =>
      feat.enabled &&
      feat.feature.enabled &&
      feat.feature.type === advanceFeatureType
  );
};

/**
 * Check if any child menu items have permission for the current user
 */
export const hasPermissionInChildren = (
  currentRole: Roles | undefined,
  userFeatures: UserFeature[] | undefined,
  items: MenuItemsType[]
): boolean => {
  if (!items?.length || !currentRole) return false;

  return items.some((item) => {
    // Check direct role permission
    const directPermission = hasPermission(currentRole, item.permissions);

    // Check advance feature permission
    const hasFeatureAccess = hasAdvanceFeatureAccess(
      currentRole,
      userFeatures,
      item.advanceFeatureType
    );

    // For items with subMenuItems, check those too
    if ("subMenuItems" in item && item.subMenuItems) {
      return (
        (directPermission && hasFeatureAccess) ||
        hasPermissionInChildren(currentRole, userFeatures, item.subMenuItems)
      );
    }

    return directPermission && hasFeatureAccess;
  });
};

/**
 * Determine if a menu item should be shown based on permissions and advance features
 */
export const shouldShowMenuItem = (
  currentRole: Roles | undefined,
  userFeatures: UserFeature[] | undefined,
  item: MenuItemsType
): boolean => {
  if (!currentRole) return false;

  // Check role permission
  const directPermission = hasPermission(currentRole, item.permissions);

  // Check advance feature permission
  const hasFeatureAccess = hasAdvanceFeatureAccess(
    currentRole,
    userFeatures,
    item.advanceFeatureType
  );

  // For parent items with children, also check child permissions
  if ("subMenuItems" in item && item.subMenuItems) {
    return (
      (directPermission && hasFeatureAccess) ||
      hasPermissionInChildren(currentRole, userFeatures, item.subMenuItems)
    );
  }

  return directPermission && hasFeatureAccess;
};

/**
 * Check if path exists in menu item or its children (recursively)
 */
export const pathExistsInMenu = (
  item: MenuItemsType,
  path: string
): boolean => {
  // Check if path matches this item's href
  if (item.href === path) return true;

  // For MenuItemsType with subMenuItems
  if ("subMenuItems" in item && item.subMenuItems) {
    return item.subMenuItems.some((subItem) => pathExistsInMenu(subItem, path));
  }

  return false;
};
